import { GetOutletsAroundMe } from "@/services/order.api";
import { toast } from "react-toastify";
import {
  updateUserLocation,
  UserLocation,
} from "@/store/slices/userLocationSlice";
import { AnyAction, Dispatch } from "redux";
import { MenuItem, ProductMenuItem } from "@/types/Menu.types";

/**
 * Generate a random integer up to the specified maximum
 */
export const getRandomInt = (max: number) => {
  return Math.floor(Math.random() * max);
};

/**
 * Get related products based on category and exclude specific IDs
 */
export const getRelatedProducts = (
  menu: MenuItem[] | ProductMenuItem[],
  category: string[],
  excludeIds: string[]
) => {
  const relatedProducts = menu.filter(
    (product) =>
      category.includes(product.category) && !excludeIds.includes(product._id)
  );
  return relatedProducts;
};

/**
 * Pick random related items for a cart item
 */
const pickRandomRelatedItems = (cartItem: MenuItem | ProductMenuItem) => {
  const maxRelatedItems = 3;

  const relatedProducts = getRelatedProducts([], [], [cartItem._id]);
  const selectedRelatedItems = [];

  while (
    selectedRelatedItems.length < maxRelatedItems &&
    relatedProducts.length > 0
  ) {
    const randomIndex = getRandomInt(relatedProducts.length);
    selectedRelatedItems.push(relatedProducts[randomIndex]);
    relatedProducts.splice(randomIndex, 1);
  }

  return selectedRelatedItems;
};

/**
 * Process cart items and add related items to each
 */
// const processCartItems = (cartItems: (MenuItem | ProductMenuItem)[]) => {
//   const cartItemsWithRelatedItems = cartItems.map((cartItem) => {
//     const relatedItems = pickRandomRelatedItems(cartItem);
//     return {
//       cartItem,
//       relatedItems,
//     };
//   });

//   return cartItemsWithRelatedItems;
// };

/**
 * Truncate text to specified length with ellipsis
 */
export const truncateText = (string: string, len: number) => {
  if (string.length > len) {
    return string.slice(0, len) + "...";
  }
  return string;
};

/**
 * Shuffle array elements randomly
 */
export const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
};

/**
 * Convert date to readable format (YYYY-MM-DD)
 */
export const readableDate = (date: Date): string =>
  new Date(date).toISOString().substring(0, 10);

/**
 * Extract error message from error object
 */
export const getErrorMessge = (
  error:
    | Error
    | { response?: { data?: { message?: string } }; message?: string }
) => {
  const message =
    (error as { response?: { data?: { message?: string } } }).response?.data
      ?.message ||
    (error as Error).message ||
    error.toString();

  return message;
};

/**
 * Display error message as toast notification
 */
export const flashError = (
  error:
    | Error
    | { response?: { data?: { message?: string } }; message?: string }
) => {
  console.error(error);
  const errorMsg = getErrorMessge(error);
  toast.error(errorMsg);
};

/**
 * Capitalize first letter of text
 */
export const capitalizeText = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Check if current time is within delivery hours (8 AM - 6 PM)
 */
export const isDeliveryTime = (): boolean => {
  const currentHour = Date.now();

  const startTime = new Date().setHours(8, 0, 0, 0);
  const endTime = new Date().setHours(18, 0, 0, 0);

  return currentHour >= startTime && currentHour <= endTime;
};

/**
 * Calculate service fee based on order amount
 */
export const calcuateServiceFee = (amount: number) => {
  let serviceFee: number;
  const percentageFee = Math.round((amount / 100) * 1.5);
  if (amount < 2500) {
    serviceFee = percentageFee;
  } else if (percentageFee > 2000) {
    serviceFee = 2000;
  } else {
    serviceFee = percentageFee + 100;
  }
  return serviceFee;
};

/**
 * Get user location using Google Places API
 */
export async function getUserLocation(
  input: HTMLInputElement | null,
  dispatch?: Dispatch<AnyAction>
) {
  // Check if Google Maps API is available
  if (typeof google === "undefined" || !google.maps || !google.maps.places) {
    console.error("Google Maps API not loaded");
    return;
  }

  if (input) {
    try {
      // Define the geographical bounds for Nigeria>
      const nigeriaBounds = new google.maps.LatLngBounds(
        new google.maps.LatLng({ lat: 6.388274, lng: 2.714183 }),
        new google.maps.LatLng({ lat: 13.707279, lng: 13.632359 })
      );

      // Initialize Google Places Autocomplete with the input element
      const autoComplete = new google.maps.places.Autocomplete(input, {
        bounds: nigeriaBounds,
        fields: ["formatted_address", "geometry", "name"],
        componentRestrictions: { country: "ng" },
      });

      autoComplete.addListener("place_changed", async () => {
        const place = autoComplete.getPlace();
        // console.log("===> user's selected location: ", place);
        const userLocation: UserLocation = {
          address: place.formatted_address,
          latitude: place.geometry?.location?.lat() || 0,
          longitude: place.geometry?.location?.lng() || 0,
          city: "",
          state: "",
        };
        AppStorage.save("location", JSON.stringify(userLocation));
        console.log("User Location: ", userLocation);
        dispatch?.(updateUserLocation(userLocation));

        if (userLocation.latitude && userLocation.longitude) {
          const res = await GetOutletsAroundMe(
            userLocation.latitude,
            userLocation.longitude
          );
          AppStorage.save("outlets", JSON.stringify(res));
        }
      });
    } catch (e) {
      console.log(e);
    }
  }
}

/**
 * Map menu items by category with predefined order
 */
export function mapItemsByCategory(items: MenuItem[] | ProductMenuItem[]) {
  const categoryOrder = [
    "all",
    "food",
    "protein",
    "pastry",
    "cakes",
    "shawarma",
    "bread",
    "ice cream",
    "drinks",
    "promo",
  ];
  const categorizedItems: Record<string, (MenuItem | ProductMenuItem)[]> = {};

  // Initialize all categories in the specified order
  categoryOrder.forEach((category) => {
    categorizedItems[category] = [];
  });

  items.forEach((item) => {
    const category = item.category.toLowerCase(); // Normalize for consistency

    // Ensure the category exists in the object and add the item
    if (categorizedItems[category]) {
      categorizedItems[category].push(item);
    }
  });

  // Populate the "all" category in the specified order
  categoryOrder.forEach((category) => {
    if (category !== "all") {
      categorizedItems.all.push(...categorizedItems[category]);
    }
  });
  return categorizedItems;
}

/**
 * Local storage utility with type safety
 */
const AppStorage = {
  save: (key: string, value: string) => {
    try {
      if (typeof window === "undefined") {
        return;
      }
      localStorage.setItem(key, value);
    } catch (error) {
      console.error("Unable to save to localStorage: ", error);
    }
  },
  get: (key: string): string | null => {
    if (typeof window === "undefined") {
      return null;
    }
    const str = localStorage.getItem(key);
    return str;
  },
  remove: (key: string) => {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.removeItem(key);
  },
  clear: () => {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.clear();
  },
};

/**
 * Format timestamp to readable date string
 */
export const formatDate = (timestamp: string) => {
  return new Date(timestamp).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default AppStorage;

/**
 * Generate initials and random color from name
 */
export function getInitialsAndColor(name: string) {
  // Split the name by space and take the first letter of each word
  const initials = name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

  // Function to generate a random hex color
  const randomColor = `#${Math.floor(Math.random() * 16777215).toString(16)}`;

  // Ensure the color is always 6 characters long
  const paddedColor =
    randomColor.length === 7 ? randomColor : randomColor + "0";

  // Return the initials and the random color
  return {
    initials,
    color: paddedColor,
  };
}
